import type { Control, UseFormSetValue } from 'react-hook-form';

import type { DataTableColumn } from '@/shared/ui/data-table';

import { getExpectedTotal } from '../lib/quantities';
import type { ConfirmArrivalTableRow } from '../lib/table-rows';
import {
  getConfirmLineKey,
  type ConfirmArrivalFormValues,
  type ConfirmArrivalQuantityField,
  type ConfirmArrivalUnitMode
} from '../model/types';
import { ConfirmArrivalLineDescription } from './confirm-arrival-line-description';
import { ConfirmArrivalLineQuantityCell, ConfirmArrivalLineRemainingCell } from './confirm-arrival-line-cells';
import { ConfirmArrivalQuantityFigure } from './confirm-arrival-quantity-figure';

interface ConfirmArrivalColumnArgs {
  control: Control<ConfirmArrivalFormValues>;
  setValue: UseFormSetValue<ConfirmArrivalFormValues>;
  unitMode: ConfirmArrivalUnitMode;
  /** Losses are rejected for customer deliveries; JSF hides the column rather than disabling it. */
  showLostColumn: boolean;
  fieldLabels: Record<ConfirmArrivalQuantityField, string>;
  lineLabel: string;
  expectedLabel: string;
  remainingLabel: string;
  isSecondaryOpen: (lineKey: string, field: ConfirmArrivalQuantityField) => boolean;
  onToggleSecondary: (lineKey: string, field: ConfirmArrivalQuantityField) => void;
}

/**
 * Columns of the lines table. Only `line` rows reach these cells — consignment
 * headers are full-width group rows. Every form-bound cell subscribes to its own
 * line, so the table itself never re-renders on a keystroke.
 */
export const getConfirmArrivalColumns = ({
  control,
  setValue,
  unitMode,
  showLostColumn,
  fieldLabels,
  lineLabel,
  expectedLabel,
  remainingLabel,
  isSecondaryOpen,
  onToggleSecondary
}: ConfirmArrivalColumnArgs): DataTableColumn<ConfirmArrivalTableRow>[] => {
  const quantityColumn = (field: ConfirmArrivalQuantityField): DataTableColumn<ConfirmArrivalTableRow> => ({
    id: field,
    header: fieldLabels[field],
    width: 'w-42.5',
    cell: (row) => {
      if (row.kind !== 'line') return null;
      const lineKey = getConfirmLineKey(row.line);
      return (
        <ConfirmArrivalLineQuantityCell
          control={control}
          setValue={setValue}
          line={row.line}
          index={row.index}
          field={field}
          label={fieldLabels[field]}
          unitMode={unitMode}
          secondaryOpen={isSecondaryOpen(lineKey, field)}
          onToggleSecondary={() => onToggleSecondary(lineKey, field)}
        />
      );
    }
  });

  return [
    {
      id: 'line',
      header: lineLabel,
      width: 'w-60',
      cell: (row) => (row.kind === 'line' ? <ConfirmArrivalLineDescription line={row.line} /> : null)
    },
    {
      id: 'expected',
      header: expectedLabel,
      width: 'w-32.5',
      cell: (row) =>
        row.kind === 'line' ? (
          <ConfirmArrivalQuantityFigure
            line={row.line}
            unitMode={unitMode}
            quantity={getExpectedTotal(row.line)}
            kind="total"
          />
        ) : null
    },
    quantityColumn('goodQty'),
    quantityColumn('damagedQty'),
    ...(showLostColumn ? [quantityColumn('lostQty')] : []),
    {
      id: 'remaining',
      header: remainingLabel,
      width: 'w-32.5',
      cell: (row) =>
        row.kind === 'line' ? (
          <ConfirmArrivalLineRemainingCell control={control} line={row.line} index={row.index} unitMode={unitMode} />
        ) : null
    }
  ];
};
